/**
 * ★★★ WT lote 1 — AGRUPAMENTO DOS ERROS, puro.
 *
 * ★ Mora fora da tela pelo motivo de sempre nesta casa: a conta é testável sem navegador, e
 * a tela não pode ser uma segunda definição do que é "o mesmo problema".
 *
 * ★★ O grupo é **`codigo` + `assunto` + `tela` + `origem`** — e essa escolha é o desenho: é a
 * MESMA chave do dedup de 60 s em `lib/erros.js`. ⚠ Se divergirem, a tela mostraria como um
 * grupo o que o dedup trata como dois, e a contagem mentiria.
 *
 * ★★★ WU-2/P1 — `assunto` ENTROU na chave junto com o dedup. ⚠⚠ Registro antigo não tem o
 * campo e cai em `'—'`: ★ os de antes do deploy continuam agrupados como estavam, e não se
 * misturam com os novos (que trazem o assunto) — o que é correto, porque **não se sabe** o
 * assunto dos antigos.
 */

export const chaveDoGrupo = (e) =>
  `${e?.codigo ?? '—'}|${e?.assunto || '—'}|${e?.tela ?? '—'}|${e?.origem ?? '—'}`;

const emMs = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const d = new Date(v);
  return Number.isNaN(+d) ? 0 : +d;
};

/**
 * Agrupa os erros de uma janela.
 *
 * @param erros  lista crua (como veio do banco)
 * @param agora  instante de referência — ⚠ injetado, nunca `Date.now()` aqui dentro: é a
 *               regra do relógio da WN, e sem ela o teste depende da hora da máquina.
 * @param horas  tamanho da janela
 */
export function agruparErros(erros, agora, horas) {
  const corte = agora - horas * 3_600_000;
  const grupos = new Map();
  for (const e of erros ?? []) {
    const quando = emMs(e.criadoEm);
    if (quando < corte) continue;
    const chave = chaveDoGrupo(e);
    const g = grupos.get(chave) ?? {
      // ★ `||` e não `??` no assunto: `''` (erro sem assunto, como render) vira `—`.
      chave, codigo: e.codigo ?? '—', assunto: e.assunto || '—', tela: e.tela ?? '—', origem: e.origem ?? '—',
      quantidade: 0, ultimoEm: 0, itens: [],
    };
    g.quantidade += 1;
    if (quando > g.ultimoEm) g.ultimoEm = quando;
    // ★ guarda só os 20 mais recentes do grupo: é o que a tela mostra ao expandir, e manter
    // 500 em memória por grupo não serve a ninguém.
    g.itens.push({ ...e, quandoMs: quando });
    g.itens.sort((a, b) => b.quandoMs - a.quandoMs);
    if (g.itens.length > 20) g.itens.length = 20;
    grupos.set(chave, g);
  }
  // ★ ordena por QUANTIDADE, não por recência: a tela existe para achar o problema que mais
  // acontece, não o último que aconteceu. Empate decide pelo mais recente.
  return [...grupos.values()].sort((a, b) => b.quantidade - a.quantidade || b.ultimoEm - a.ultimoEm);
}

/** Total de erros na janela — o número que vai no cabeçalho. */
export const contarNaJanela = (erros, agora, horas) =>
  agruparErros(erros, agora, horas).reduce((n, g) => n + g.quantidade, 0);

/**
 * ★★★ WT lote 1b — SEPARA ERRO DE SISTEMA DE ERRO DE USO.
 *
 * ⚠⚠ Nasceu do primeiro registro real: `invalid-argument` de digitação. ★ Com dez clínicas,
 * esse ruído **soterra** o `permission-denied` que importa.
 *
 * ★★ Registro antigo **sem `tipo` conta como `'sistema'`** — e isso é escolha, não reserva
 * preguiçosa: os registros de antes do 1b vieram de listeners e de render, que são sistema
 * por definição. ⚠ E errar para "sistema" é o lado certo: erro de uso tratado como sistema
 * **aparece e se explica**; erro de sistema escondido na aba de uso **não aparece nunca**.
 */
export const tipoDoErro = (e) => (e?.tipo === 'uso' ? 'uso' : 'sistema');

/**
 * Agrupa em DOIS blocos. ★ A chave de agrupamento continua `codigo + tela + origem` — a
 * mesma do dedup de 60 s.
 *
 * ⚠ A ordem do retorno é fixa: **sistema primeiro**. É o que o superadmin abre a tela para
 * ver, e deixar "uso" em cima faria o ruído ganhar a atenção.
 */
export function agruparPorTipo(erros, agora, horas) {
  const sistema = agruparErros((erros ?? []).filter((e) => tipoDoErro(e) === 'sistema'), agora, horas);
  const uso = agruparErros((erros ?? []).filter((e) => tipoDoErro(e) === 'uso'), agora, horas);
  const total = (g) => g.reduce((n, x) => n + x.quantidade, 0);
  return [
    { id: 'sistema', rotulo: 'Erros do sistema', grupos: sistema, total: total(sistema) },
    { id: 'uso', rotulo: 'Erros de uso', grupos: uso, total: total(uso) },
  ];
}
